//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import { useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import SimpleBar from 'simplebar-react';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { getCloudFunctions, logEvent } from '../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import useSimpleBarRef from '../common/hooks/useSimpleBarRef';
import {
    ANALYTICS_EVENT_EMP_INVITE_ACCEPTED,
    EMP_ACC_LINKAGE_STATUS_CODE_INVITATION_ALREADY_ACCEPTED,
    EMP_ACC_LINKAGE_STATUS_CODE_INVITATION_INVALID,
    EMP_ACC_LINKAGE_STATUS_CODE_INVITEE_ACCOUNT_NOT_EXISTS,
    EMP_ACC_LINKAGE_STATUS_CODE_JOB_SEEKER_INVITEE,
    EMP_ACC_LINKAGE_STATUS_CODE_SUCCESSFUL,
} from '../common/constants';
// import { AUTH_MODE_VERIFY_EMAIL, PATH_HOME } from '../system/constants';
// import { applyActionCode } from '../common/services/authServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import TopNav from '../topNav/TopNav';
import PageMessage from '../common/elemental/PageMessage';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    pageContainer: { backgroundColor: '#FAFAFC', overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
    pageHeader: {
        maxHeight: '81px',
        // [theme.breakpoints.down('md')]: { maxHeight: '134px' }, // commented out because no more second row menu -el 20221022
        // [theme.breakpoints.down('sm')]: { maxHeight: '81px' },
    },
    pageContent: {
        flex: 1,
        overflowY: 'hidden',
    },
    tempContainer: { alignItems: 'center', marginTop: 50 },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        // currentPageId,
        // setCurrentPageId,
    } = params;

    const defns = {
        pageContainer: { container: true, direction: 'column' },
        pageHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        pageContent: { item: true, container: true },
        tempContainer: { item: true, container: true, direction: 'column' },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// // A custom hook that builds on useLocation to parse the query string for you.
// const useQuery = () => {
//     const { search } = useLocation();

//     return React.useMemo(() => new URLSearchParams(search), [search]);
// };

const PAGE_ID_PROCESSING_INVITATION_CODE = 1;
const PAGE_ID_INVITATION_INVALID_OR_ALREADY_ACCEPTED = 2;
const PAGE_ID_JOB_SEEKER_INVITEE = 3;
const PAGE_ID_INVITEE_ACCOUNT_NOT_EXISTS = 4;
const PAGE_ID_SUCCESSFUL = 5;
const PAGE_ID_HTTPS_ERROR = 6;
// const PAGE_ID_ACTION_CODE_FAILED_EMAIL_VERIFICATION = 5;

// const PAGE_ID_REDIRECT_TO_HOME_PAGE = 99;

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const EmpInviteHandlerLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const appContext = useContext(AppContext);
    const { auth, authUser } = appContext;
    const context = useContext(LayoutContext);
    // const { setLoadingDialogOpen } = context;
    const { simpleBarRef: pageContentSimpleBarEl, scrollToTop: scrollToPageTop } = useSimpleBarRef();

    const { code } = useParams();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const {  } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isShowProgressBar, setIsShowProgressBar] = useState(false);
    const [currentPageId, setCurrentPageId] = useState();
    const [invitationData, setInvitationData] = useState();
    const [isRequestOpenLoginDialog, setIsRequestOpenLoginDialog] = useState();
    const [isRequestOpenCreateAccountDialog, setIsRequestOpenCreateAccountDialog] = useState();
    const [isRequestLogOut, setIsRequestLogOut] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        const processCode = async ({ code } = {}) => {
            setCurrentPageId(PAGE_ID_PROCESSING_INVITATION_CODE);
            try {
                const functions = getCloudFunctions();
                const fetchEmpInvitationDetailByCode = functions.httpsCallable('fetchEmpInvitationDetailByCode');
                setIsShowProgressBar(true);
                const result = await fetchEmpInvitationDetailByCode({ code, autoLink: true });
                setIsShowProgressBar(false);
                const status = _get(result, 'data.status');
                const invitation = _get(result, 'data.invitation');
                switch (status) {
                    case EMP_ACC_LINKAGE_STATUS_CODE_INVITATION_INVALID:
                    case EMP_ACC_LINKAGE_STATUS_CODE_INVITATION_ALREADY_ACCEPTED: {
                        setCurrentPageId(PAGE_ID_INVITATION_INVALID_OR_ALREADY_ACCEPTED);
                        break;
                    }
                    case EMP_ACC_LINKAGE_STATUS_CODE_JOB_SEEKER_INVITEE: {
                        setCurrentPageId(PAGE_ID_JOB_SEEKER_INVITEE);
                        break;
                    }
                    case EMP_ACC_LINKAGE_STATUS_CODE_INVITEE_ACCOUNT_NOT_EXISTS: {
                        setCurrentPageId(PAGE_ID_INVITEE_ACCOUNT_NOT_EXISTS);
                        setIsRequestLogOut(true);
                        break;
                    }
                    case EMP_ACC_LINKAGE_STATUS_CODE_SUCCESSFUL: {
                        setCurrentPageId(PAGE_ID_SUCCESSFUL);
                        logEvent(ANALYTICS_EVENT_EMP_INVITE_ACCEPTED);
                        break;
                    }
                    default:
                    // do nothing
                }
                if (invitation) {
                    setInvitationData(invitation);
                }
            } catch (error) {
                console.log('Error fetching invitation data', error);
                setCurrentPageId(PAGE_ID_HTTPS_ERROR);
            }
        };
        if (code) {
            processCode({ code });
        }
    }, [code, auth, authUser]);

    useEffect(() => {
        if (isRequestLogOut) {
            if (auth && authUser) {
                auth.signOut();
            }
            setIsRequestLogOut(false);
        }
    }, [isRequestLogOut, setIsRequestLogOut, auth, authUser]);

    // useEffect(() => {
    //     if (invitationData) {
    //         // alert(`invitationData is: ${JSON.stringify(invitationData)}`);
    //     }
    // }, [invitationData]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const getPageComponent = () => {
        switch (currentPageId) {
            case PAGE_ID_INVITATION_INVALID_OR_ALREADY_ACCEPTED:
                return <PageMessage invitationInvalidOrAccepted />;
            case PAGE_ID_JOB_SEEKER_INVITEE:
                return <PageMessage jobSeekerInvitee />;
            case PAGE_ID_INVITEE_ACCOUNT_NOT_EXISTS:
                return <PageMessage inviteeAccountNotExist />;
            case PAGE_ID_SUCCESSFUL:
                return <PageMessage successful />;
            case PAGE_ID_HTTPS_ERROR:
                return <PageMessage httpsError />;
            default:
                // do nothing
                break;
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, currentPageId, setCurrentPageId });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider
            value={{
                ...context,
                scrollToPageTop,
                invitationData,
                invitationCode: code,
                isRequestOpenLoginDialog,
                setIsRequestOpenLoginDialog,
                isRequestOpenCreateAccountDialog,
                setIsRequestOpenCreateAccountDialog,
            }}
        >
            <Grid {...defns.pageContainer}>
                <Grid {...defns.pageHeader}>
                    <TopNav isShowProgressBar={isShowProgressBar} />
                </Grid>
                <Grid {...defns.pageContent}>
                    <SimpleBar style={{ height: '100%', width: '100%', flex: 1 }} ref={pageContentSimpleBarEl}>
                        {getPageComponent()}
                    </SimpleBar>
                </Grid>
            </Grid>
        </LayoutContext.Provider>
    );
};

export default EmpInviteHandlerLayout;
